import _ from 'lodash'
import { makeStyles } from '@material-ui/core'
import {
  Control,
  DeepPartial,
  FormState,
  OmitResetState,
  UnpackNestedValue,
} from 'react-hook-form'
import {
  EnergySystemFragment,
  EnergySystemType,
  ScheduleFragment,
  UpdateEnergySystemInput,
  UpdateScheduleInput,
} from '../../../../generated/graphql'
import {
  mapFormValuesToInput as esFormValuesToInput,
  mapInputToFormValues as esSystemInputToForm,
  FormValues as EsSystemFormValues,
} from '../../energy-system/form-values'
import {
  mapFormValuesToInput as scheduleFormValuesToInput,
  mapFormValuesToInputForCalibration as scheduleCalFormValuesToInput,
  mapInputToFormValues as scheduleInputToForm,
  FormValues as ScheduleFormValues,
} from '../../schedule/form-values'

export type InputTabFormValues = {
  values: EsSystemFormValues
  scheduleValues?: ScheduleFormValues
}

export const useInputTabStyles = makeStyles((themeSpec) => ({
  inputTab: {
    border: 'solid',
    borderWidth: 'thin',
    borderColor: 'lightgray',
    padding: themeSpec.spacing(2),
  },
}))

export const convertInputParametersToFormParameters = (
  esSystem: EnergySystemFragment | undefined,
  schedule: ScheduleFragment | undefined,
  modelArea: number,
): InputTabFormValues | undefined => {
  if (!esSystem) return undefined

  const result: InputTabFormValues = {
    values: esSystemInputToForm(esSystem, modelArea),
    scheduleValues: undefined,
  }
  if (schedule) result.scheduleValues = scheduleInputToForm(schedule)

  return result
}

export const updateScheduleFormValuesForDbUpdate = (
  values: ScheduleFormValues,
  defaultValues: ScheduleFormValues | undefined,
): ScheduleFormValues => {
  if (!defaultValues) return values

  const updatedValues = { ...values }

  updatedValues.name = defaultValues.name
  updatedValues.hourlyExceptions = defaultValues.hourlyExceptions
  updatedValues.weekdayValues = defaultValues.weekdayValues
  updatedValues.exceptionInUse = defaultValues.exceptionInUse
  updatedValues.exceptionStart = defaultValues.exceptionStart
  updatedValues.exceptionEnd = defaultValues.exceptionEnd
  updatedValues.exceptionProfile = defaultValues.exceptionProfile

  return updatedValues
}

type UpdateModelProps = {
  values: InputTabFormValues
  defaultValues: InputTabFormValues
  esType: EnergySystemType
  control: Control<InputTabFormValues>
  formState: FormState<InputTabFormValues>
  updateStateRef: React.MutableRefObject<boolean>
  onReset: (
    values?: UnpackNestedValue<DeepPartial<InputTabFormValues>>,
    omitResetState?: OmitResetState,
  ) => void
  onUpdate: (
    esInput: UpdateEnergySystemInput,
    scheduleId?: string,
    scheduleInput?: UpdateScheduleInput,
  ) => void
}

export const updateModel = async ({
  values,
  defaultValues,
  control,
  formState,
  updateStateRef,
  onReset,
  onUpdate,
}: UpdateModelProps): Promise<void> => {
  const { isDirty } = formState
  const isValid = await control.trigger()
  if (isValid && isDirty) {
    const updateValues = { ...defaultValues }
    _.merge(updateValues, values)

    const energySystemInput = esFormValuesToInput(updateValues.values)
    const { scheduleId } = updateValues.values
    const scheduleInput = updateValues.scheduleValues
      ? scheduleCalFormValuesToInput(updateValues.scheduleValues)
      : undefined

    onUpdate(energySystemInput, scheduleId, scheduleInput)

    onReset(updateValues)

    updateStateRef.current = true // eslint-disable-line no-param-reassign
  }
}

type SaveInputDataProps = {
  energySystemId: string
  control: Control<InputTabFormValues>
  onDbUpdate: (
    esId: string,
    esInput: UpdateEnergySystemInput,
    scheduleIds?: string[],
    scheduleInputs?: UpdateScheduleInput[],
  ) => Promise<void>
}

export const saveInputData = async ({
  energySystemId,
  control,
  onDbUpdate,
}: SaveInputDataProps): Promise<void> => {
  if (!control.defaultValuesRef.current) return

  const currentValues = control.defaultValuesRef.current as InputTabFormValues

  const energySystemInput = esFormValuesToInput(currentValues.values)

  let scheduleInput: UpdateScheduleInput | undefined

  if (currentValues.scheduleValues)
    scheduleInput = scheduleFormValuesToInput(currentValues.scheduleValues)

  if (currentValues.values.scheduleId && scheduleInput)
    await onDbUpdate(
      energySystemId,
      energySystemInput,
      [currentValues.values.scheduleId],
      [scheduleInput],
    )
  else await onDbUpdate(energySystemId, energySystemInput)
}
